import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Button, StandardLayout } from '@factorialco/f0-react'
import { OneModal, Tabs } from '@factorialco/f0-react/dist/experimental'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'
import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

import { CompensationTab } from './CompensationTab'
import { EmployeeHeader } from './EmployeeHeader'
import { OverviewTab } from './OverviewTab'
import type { TabId } from './types'
import { useEmployeeApproval } from './useEmployeeApproval'
import { useEmployeeStatus } from './useEmployeeStatus'
import { usePrimaryAction } from './usePrimaryAction'

interface Props {
  employee: AttendanceCustomPeriodEmployeeItemFragment | null
  onClose: () => void
  onEmployeeUpdate?: (updatedEmployee: AttendanceCustomPeriodEmployeeItemFragment) => void
}

interface SidepanelContentProps {
  employee: AttendanceCustomPeriodEmployeeItemFragment
  onClose: () => void
  onEmployeeUpdate?: (updatedEmployee: AttendanceCustomPeriodEmployeeItemFragment) => void
}

const SidepanelContent = ({ employee, onClose, onEmployeeUpdate }: SidepanelContentProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const { featureAvailability: hasCompensationFlowV2 } = useAttendanceFeatures(
    'dev_time_tracking_compensation_flow_v2'
  )
  const [selectedCompensationBlocks, setSelectedCompensationBlocks] = useState<
    AttendanceSidepanelCompensationBlockFragment[]
  >([])

  const { cycle, dateFrom, dateTo } = useAttendanceDateNavigator()

  const { handleApprove, handleDisapprove } = useEmployeeApproval({
    employee,
    cycle,
    dateFrom,
    dateTo,
    onEmployeeUpdate,
  })

  const status = useEmployeeStatus(employee)
  const primaryAction = usePrimaryAction({
    employee,
    approvePeriod: handleApprove,
    disapprovePeriod: handleDisapprove,
    activeTab,
    selectedCompensationBlocks,
  })

  const handleOpenTimesheet = () => {
    navigate(
      navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
        periodType: cycle,
        year: Number(dateFrom.getFullYear()),
        month: Number(dateFrom.getMonth() + 1),
        day: Number(dateFrom.getDate()),
        employeeId: employee.id ?? '',
      })
    )
  }
  const isDaily = Boolean(employee.referenceContract?.frForfaitJours)

  return (
    <OneModal isOpen onClose={onClose} position="right">
      <OneModal.Header title={i18n.t('time_tracking.table_collection.employee_summary.title')} />
      <OneModal.Content>
        <StandardLayout>
          <EmployeeHeader
            employee={employee}
            status={status}
            isDaily={isDaily}
            onOpenTimesheet={handleOpenTimesheet}
          />
          {hasCompensationFlowV2 ? (
            <>
              <Tabs
                activeTabId={activeTab}
                tabs={[
                  {
                    index: true,
                    id: 'overview',
                    label: i18n.t('time_tracking.table_collection.employee_summary.tabs.overview'),
                    onClick: () => setActiveTab('overview'),
                  },
                  {
                    id: 'compensation',
                    label: i18n.t(
                      'time_tracking.table_collection.employee_summary.tabs.compensation'
                    ),
                    onClick: () => setActiveTab('compensation'),
                  },
                ]}
              />
              {activeTab === 'overview' && <OverviewTab employee={employee} />}
              {activeTab === 'compensation' && employee.id && (
                <CompensationTab
                  employeeId={employee.id}
                  setSelectedCompensationBlocks={setSelectedCompensationBlocks}
                />
              )}
            </>
          ) : (
            <OverviewTab employee={employee} />
          )}
        </StandardLayout>
      </OneModal.Content>
      <OneModal.Footer>
        {primaryAction && (
          <F0Button
            onClick={primaryAction.onClick}
            type="primary"
            label={primaryAction.label}
            icon={primaryAction.icon}
          />
        )}
      </OneModal.Footer>
    </OneModal>
  )
}

const CustomPeriodEmployeeSidepanel = ({ employee, onClose, onEmployeeUpdate }: Props) => {
  if (!employee) return null

  return (
    <SidepanelContent employee={employee} onClose={onClose} onEmployeeUpdate={onEmployeeUpdate} />
  )
}

export default CustomPeriodEmployeeSidepanel
